import { format } from 'date-fns';
import type { Timestamp } from 'firebase/firestore';
import { createDateSafe } from '@/utils/dateUtils';
import type { ApprovalStatus, SellRequestStatus, VacationRequest, VacationSegment } from '../models/vacation.types';
import { getSegmentCompletedLabel, getSegmentStartedLabel, TIMELINE_LABELS } from '../vacations.definitions';
import {
    isApprovedVacationRequest,
    isDraftVacationRequest,
    resolveManagerEmail,
    resolveDraftGraceEnd,
} from './vacationRequestStatus';

export type VacationTimelineTone = 'done' | 'pending' | 'rejected' | 'muted';

export interface VacationTimelineEvent {
    key: string;
    label: string;
    tone: VacationTimelineTone;
    occurredAt: Date | null;
    hasTime: boolean;
    actorEmail: string | null;
    note: string | null;
}

type DecisionStatus = ApprovalStatus | SellRequestStatus;

const DECISION_TONES: Record<DecisionStatus, VacationTimelineTone> = {
    None: 'pending',
    Pending: 'pending',
    Approved: 'done',
    Rejected: 'rejected',
};

interface DecisionEventInput {
    key: string;
    status: DecisionStatus;
    labels: Record<'Pending' | 'Approved' | 'Rejected', string>;
    at?: Timestamp | null;
    actorEmail?: string | null;
    note?: string | null;
}

function toDateOrNull(value?: Timestamp | null): Date | null {
    return value?.toDate?.() ?? null;
}

function buildDecisionEvent({
    key,
    status,
    labels,
    at,
    actorEmail,
    note,
}: DecisionEventInput): VacationTimelineEvent {
    return {
        key,
        label: status === 'Approved' || status === 'Rejected' ? labels[status] : labels.Pending,
        tone: DECISION_TONES[status],
        occurredAt: toDateOrNull(at),
        hasTime: true,
        actorEmail: actorEmail ?? null,
        note: note ?? null,
    };
}

function buildDateEvent(key: string, label: string, isoDate?: string): VacationTimelineEvent {
    return {
        key,
        label,
        tone: 'done',
        occurredAt: isoDate ? createDateSafe(isoDate) : null,
        hasTime: false,
        actorEmail: null,
        note: null,
    };
}

function sortedSegments(segments: VacationSegment[]): VacationSegment[] {
    return [...segments]
        .filter((segment) => Boolean(segment?.startDate))
        .sort((a, b) => a.startDate.localeCompare(b.startDate));
}

function buildFruitionEvents(request: VacationRequest, today: Date): VacationTimelineEvent[] {
    if (!isApprovedVacationRequest(request)) return [];

    const segments = sortedSegments(request.segments);
    if (segments.length === 0) return [];

    const todayIso = format(today, 'yyyy-MM-dd');
    const forceCompleted = request.status === 'Completed';
    const events: VacationTimelineEvent[] = [];

    segments.forEach((segment, index) => {
        const position = index + 1;

        if (forceCompleted || segment.startDate <= todayIso) {
            events.push(
                buildDateEvent(
                    `started-${position}`,
                    getSegmentStartedLabel(index, segments.length),
                    segment.startDate
                )
            );
        }

        if (segment.endDate && (forceCompleted || segment.endDate < todayIso)) {
            events.push(
                buildDateEvent(
                    `completed-${position}`,
                    getSegmentCompletedLabel(index, segments.length),
                    segment.endDate
                )
            );
        }
    });

    return events;
}

export function buildVacationTimeline(request: VacationRequest, today: Date = new Date()): VacationTimelineEvent[] {
    if (isDraftVacationRequest(request)) {
        return [
            {
                key: 'requested',
                label: TIMELINE_LABELS.draftRequested,
                tone: 'done',
                occurredAt: toDateOrNull(request.requestedAt),
                hasTime: true,
                actorEmail: request.requestedBy ?? null,
                note: null,
            },
            {
                key: 'draftPending',
                label: TIMELINE_LABELS.draftPending,
                tone: 'pending',
                occurredAt: resolveDraftGraceEnd(request),
                hasTime: true,
                actorEmail: null,
                note: null,
            },
        ];
    }

    const events: VacationTimelineEvent[] = [
        {
            key: 'requested',
            label: TIMELINE_LABELS.requested,
            tone: 'done',
            occurredAt: toDateOrNull(request.requestedAt),
            hasTime: true,
            actorEmail: request.requestedBy ?? null,
            note: null,
        },
        buildDecisionEvent({
            key: 'manager',
            status: request.managerApproval.status,
            labels: {
                Pending: TIMELINE_LABELS.managerPending,
                Approved: TIMELINE_LABELS.managerApproved,
                Rejected: TIMELINE_LABELS.managerRejected,
            },
            at: request.managerApproval.at,
            actorEmail: request.managerApproval.by ?? resolveManagerEmail(request),
            note: request.managerApproval.note,
        }),
    ];

    const clientVisible =
        (request.managerApproval.status === 'Approved' || request.clientApproval.status !== 'None') &&
        request.clientApproval.status !== 'None';

    if (clientVisible) {
        events.push(
            buildDecisionEvent({
                key: 'client',
                status: request.clientApproval.status === 'Approved'
                    ? 'Approved'
                    : request.clientApproval.status === 'Rejected'
                        ? 'Rejected'
                        : 'Pending',
                labels: {
                    Pending: TIMELINE_LABELS.clientPending,
                    Approved: TIMELINE_LABELS.clientApproved,
                    Rejected: TIMELINE_LABELS.clientRejected,
                },
                at: request.clientApproval.at,
                actorEmail: request.clientApproval.by,
            })
        );
    }

    const dpVisible =
        request.managerApproval.status === 'Approved' || request.dpApproval.status !== 'Pending';

    if (dpVisible) {
        events.push(
            buildDecisionEvent({
                key: 'dp',
                status: request.dpApproval.status,
                labels: {
                    Pending:
                        request.status === 'PendingCorrection'
                            ? TIMELINE_LABELS.correctionPending
                            : TIMELINE_LABELS.dpPending,
                    Approved: TIMELINE_LABELS.dpApproved,
                    Rejected: TIMELINE_LABELS.dpRejected,
                },
                at: request.dpApproval.at,
                actorEmail: request.dpApproval.by,
                note: request.dpApproval.note,
            })
        );
    }

    if (request.sellRequest.requested) {
        events.push(
            buildDecisionEvent({
                key: 'sell',
                status: request.sellRequest.status,
                labels: {
                    Pending: TIMELINE_LABELS.sellPending,
                    Approved: TIMELINE_LABELS.sellApproved,
                    Rejected: TIMELINE_LABELS.sellRejected,
                },
                at: request.sellRequest.decidedAt,
                actorEmail: request.sellRequest.decidedBy,
                note: request.sellRequest.note,
            })
        );
    }

    if (request.signature.sentAt) {
        events.push({
            key: 'signatureSent',
            label: TIMELINE_LABELS.signatureSent,
            tone: 'done',
            occurredAt: toDateOrNull(request.signature.sentAt),
            hasTime: true,
            actorEmail: null,
            note: null,
        });
    }

    if (request.signature.signedAt) {
        events.push({
            key: 'signatureSigned',
            label: TIMELINE_LABELS.signatureSigned,
            tone: 'done',
            occurredAt: toDateOrNull(request.signature.signedAt),
            hasTime: true,
            actorEmail: null,
            note: null,
        });
    }

    events.push(...buildFruitionEvents(request, today));

    return events;
}
